/* STEP 3 · Rule 8 (B-8a / B-8b) — the ONE role-list module of the screens. It holds NO list of its own: it is fed by
 * GET /api/review-roles (backend review_roles.py — the named sets, the action → set map and the title aliases, e.g.
 * "Director" = Partner). Until the answer is in, every check answers false (a button shows only when the server's own list
 * admits the role — never a guessed list). Who may sign a level / stage is per engagement: GET
 * /api/engagements/{id}/signoff-rights (review_roles.may_sign), read by the stage card. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { asArray } from "@/lib/asArray";

let cache = null;
let inflight = null;
const subs = new Set();

export function loadReviewRoles() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = Promise.resolve()
      .then(() => api.get("/review-roles", { _silent: true }))
      .then((r) => {
        const d = r && r.data;
        if (d && d.actions && typeof d.actions === "object") {
          cache = d;
          subs.forEach((f) => f(d));
        }
        inflight = null;
        return cache;
      })
      .catch(() => { inflight = null; return null; });
  }
  return inflight;
}

export function canonicalRole(data, role) {
  const a = (data && data.aliases) || {};
  return a[role] || role;
}

/** true when ``role`` is in the server's set for ``action`` (false while the sets are not loaded). */
export function roleAllowed(data, action, role) {
  if (!data || !role) return false;
  return asArray((data.actions || {})[action]).includes(canonicalRole(data, role));
}

/** true when ``role`` is in the named set ``name`` (e.g. "TEAM_ROLES"). */
export function roleInSet(data, name, role) {
  if (!data || !role) return false;
  return asArray((data.sets || {})[name]).includes(canonicalRole(data, role));
}

export function useReviewRoles() {
  const [data, setData] = useState(cache);
  useEffect(() => {
    let on = true;
    const f = (d) => { if (on) setData(d); };
    subs.add(f);
    if (cache) setData(cache); else loadReviewRoles();
    return () => { on = false; subs.delete(f); };
  }, []);
  return { ready: !!data, data, can: (action, role) => roleAllowed(data, action, role) };
}

/** tests only: start from a known payload (or null). */
export function __setReviewRolesForTest(d) { cache = d || null; inflight = null; }
